/**
 * API Service for Commission Plan
 * Handles API calls with authentication token from parent application
 */

import { useMemo } from 'react'
import { useAuth } from '@/contexts/auth-context'
import { env } from './env'
import { tokenManager } from './token-manager'
import { getBasePathUrl } from './base-path'
import { requestTokenDueToAuthFailure } from './app-messaging'

// API Endpoints configuration
const API_ENDPOINTS = {
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
  },
  PUBLIC: {
    BASE_URL: env.apiUrl,
  },
  COMMISSION: { 
    BASE_URL: env.commissionServiceEndpoint || 'http://localhost:44321',
  },
}

const createUrl = (baseUrl: string, path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${baseUrl}${cleanPath}`
}

export const useApi = () => {

    const {token} = useAuth()
    // Generic fetch function with authentication and token retry logic
    const fetchWithAuth = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
      const headers = new Headers(options.headers)
  
      // Add authorization header if token exists
      if (token) {
        console.log('🔐 Adding Authorization header: token available')
        headers.set("Authorization", `Bearer ${token}`)
      } else {
        console.warn('⚠️ No token available for API request to:', url)
      }
  
      // Apply base path if the URL is relative and doesn't start with http or //
      let finalUrl = url
      if (!url.startsWith("http") && !url.startsWith("//")) {
        finalUrl = getBasePathUrl(url)
      }
      
      console.log('📡 Final API URL:', finalUrl)
      // Note: Not logging headers to avoid leaking bearer tokens
  
      try {
        const response = await fetch(finalUrl, {
          ...options,
          headers,
        })
  
        if (!response.ok) {
          // Handle 401 Unauthorized errors
          if (response.status === 401) {
            console.warn("Received 401 Unauthorized from", finalUrl)
  
            // Only request a new token if we haven't already requested one
            if (tokenManager.shouldRequestToken()) {
              console.log("Requesting new token from parent")
              requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
            }
  
            // Queue this request to be retried when we get a new token
            console.log("Queueing request for retry when new token is received")
            const retriedResponse = await tokenManager.queueRequest(finalUrl, {
              ...options,
              headers,
            })
  
            // Process the retried response
            if (!retriedResponse.ok) {
              const errorData = await retriedResponse.json().catch(() => ({}))
              throw new Error(errorData.message || `API request failed with status ${retriedResponse.status}`)
            }
  
            // For non-JSON responses, return the response object
            const contentType = retriedResponse.headers.get("content-type")
            if (contentType && contentType.includes("application/json")) {
              return retriedResponse.json()
            }
  
            return retriedResponse as unknown as T
          }
  
          // Handle other error responses
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `API request failed with status ${response.status}`)
        }
  
        // For non-JSON responses, return the response object
        const contentType = response.headers.get("content-type")
        if (contentType && contentType.includes("application/json")) {
          return response.json()
        }
  
        return response as unknown as T
      } catch (error) {
        // Rethrow the error
        throw error
      }
    }

    const get = <T,>(endpoint: string, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, { ...options, method: "GET" })
  
    const post = <T,>(endpoint: string, data: any, options: RequestInit = {}) => {
      const init: RequestInit = { ...options, method: "POST" }
      const headers = new Headers(options.headers ?? {})
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json")
      }
      init.headers = headers
      init.body = data !== undefined ? JSON.stringify(data) : undefined
      return fetchWithAuth<T>(endpoint, init)
    }
  
    const put = <T,>(endpoint: string, data: any, options: RequestInit = {}) => {
      const init: RequestInit = { ...options, method: "PUT" }
      const headers = new Headers(options.headers ?? {})
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json")
      }
      init.headers = headers
      init.body = data !== undefined ? JSON.stringify(data) : undefined
      return fetchWithAuth<T>(endpoint, init)
    }
  
    const patch = <T,>(endpoint: string, data: any, options: RequestInit = {}) => {
      const init: RequestInit = { ...options, method: "PATCH" }
      const headers = new Headers(options.headers ?? {})
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json")
      }
      init.headers = headers
      init.body = data !== undefined ? JSON.stringify(data) : undefined
      return fetchWithAuth<T>(endpoint, init)
    }
  
    const del = <T,>(endpoint: string, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })

  // API service instances

  const adminApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data?: any, options?: RequestInit) => post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data?: any, options?: RequestInit) => put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) => patch<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }

  const publicApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.PUBLIC.BASE_URL, path), options),
  }

  const commissionPlanApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), options),
    post: <T,>(path: string, data?: any, options?: RequestInit) => post<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), data, options),
    put: <T,>(path: string, data?: any, options?: RequestInit) => put<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) => patch<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), options),
  }

  // Memoize on token so the client objects are stable across renders. Without this, useApi() returns
  // fresh objects every render, which makes them unstable useEffect dependencies (e.g. an effect that
  // depends on adminApi would re-run every render and can loop). The clients only close over `token`.
  return useMemo(() => ({
    adminApi,
    publicApi,
    commissionPlanApi,
  }), [token])
}

